import React, { Component, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  resetOnPropsChange?: boolean
  resetKeys?: Array<string | number>
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
  prevResetKeys?: Array<string | number>
}

export default class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null

  constructor(props: Props) {
    super(props)
    this.state = { 
      hasError: false,
      prevResetKeys: props.resetKeys
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    const { resetKeys } = props
    const { prevResetKeys, hasError } = state

    // Reset error state if resetKeys have changed
    if (hasError && resetKeys && prevResetKeys) {
      if (resetKeys.some((key, idx) => key !== prevResetKeys[idx])) {
        return {
          hasError: false,
          error: undefined,
          errorInfo: undefined,
          prevResetKeys: resetKeys
        }
      }
    }

    if (resetKeys !== prevResetKeys) {
      return { prevResetKeys: resetKeys }
    }

    return null
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
    
    this.setState({ errorInfo })
    
    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo)

    // Auto-reset after 10 seconds in development
    if (process.env.NODE_ENV === 'development') {
      this.resetTimeoutId = window.setTimeout(() => {
        this.handleReset()
      }, 10000)
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId)
    }
  }

  handleReset = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId)
      this.resetTimeoutId = null
    }
    
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="error-boundary" style={{
          padding: '2rem',
          margin: '1rem',
          backgroundColor: 'rgba(255, 82, 82, 0.1)',
          border: '1px solid var(--accent-red)',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '3rem',
            marginBottom: '1rem'
          }}>
            💥
          </div>
          
          <h2 style={{
            color: 'var(--accent-red)',
            marginBottom: '1rem'
          }}>
            Something went wrong
          </h2>
          
          <p style={{
            color: 'var(--text-light)',
            marginBottom: '2rem',
            lineHeight: 1.5
          }}>
            An unexpected error occurred. You can try refreshing the page or the error might resolve automatically.
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            marginBottom: '2rem'
          }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--primary-blue)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Try Again
            </button>
            
            <button
              onClick={this.handleReload}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'transparent',
                color: 'var(--text-light)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Refresh Page
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ 
              marginTop: '1rem', 
              textAlign: 'left',
              backgroundColor: 'var(--page-bg)',
              padding: '1rem',
              borderRadius: '4px',
              border: '1px solid var(--border-color)'
            }}>
              <summary style={{ 
                cursor: 'pointer',
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                color: 'var(--text-light)'
              }}>
                Error Details (Development Only)
              </summary>
              
              <div style={{ marginBottom: '1rem' }}>
                <strong>Error:</strong> {this.state.error.message}
              </div>
              
              {this.state.error.stack && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Stack Trace:</strong>
                  <pre style={{ 
                    fontSize: '0.8rem', 
                    overflow: 'auto',
                    backgroundColor: 'var(--container-bg)',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    marginTop: '0.5rem',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {this.state.error.stack}
                  </pre>
                </div>
              )}
              
              {this.state.errorInfo && (
                <div>
                  <strong>Component Stack:</strong>
                  <pre style={{ 
                    fontSize: '0.8rem', 
                    overflow: 'auto',
                    backgroundColor: 'var(--container-bg)',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    marginTop: '0.5rem',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}